import { CartAddEvent } from '@theme/events';
import {
  formatMoney,
  escapeHtml,
  getDefaultList,
  onListChange,
  removeFromWishlist,
} from '@theme/wishlist-core';

const SS_PRODUCTS_KEY = 'swym-yokomo-wishlist-products';

/**
 * ほしい物リストページ本体。
 * マークアップは `sections/swym-wishlist-page-yokomo.liquid`、
 * 保存先は `assets/swym-wishlist-core-yokomo.js`（`localStorage`）。
 *
 * リストの読み書きは必ずコア経由にする（このページが直接 `localStorage` を触らない）。
 * ヘッダーバッジ・PDP ボタンと同じ状態を `getDefaultList()`/`onListChange()` で共有し、
 * 削除は `removeFromWishlist()` に投げてその結果通知を受け取る。
 *
 * 保存されているのは追加時のスナップショット（`iu`/`dt`/`pr`）で、初期表示の
 * フォールバックにのみ使う。実際の価格・在庫は都度 `{du}.js` から Shopify の
 * 最新データを取得して描画する。
 */
class SwymWishlistPageYokomo extends HTMLElement {
  #productJSONCache = {};
  /** @type {(() => void) | null} */
  #unsubscribe = null;
  /** @type {string | null} */
  #lid = null;

  /**
   * `#renderProducts` の呼び出しごとに増える世代カウンタ。初期表示はキャッシュからの
   * 描画と実際の取得結果からの描画を並行に走らせる（両方 await しない）ため、
   * 両方が非同期の商品 JSON 取得を終えた後に構わず `appendChild` すると、後着した方が
   * 先着した方の上に重ねて追加されカードが二重に出る。各呼び出しに世代を振り、
   * 自分より新しい世代が走っていれば描画を諦めることで「最後に呼ばれたものだけが
   * DOM に反映される」ようにする。
   * @type {number}
   */
  #renderGeneration = 0;

  connectedCallback() {
    this.grid = this.querySelector('[data-ref="grid"]');
    this.loading = this.querySelector('[data-ref="loading"]');
    this.empty = this.querySelector('[data-ref="empty"]');
    this.countEl = this.querySelector('[data-ref="count"]');

    if (!this.grid) return;

    // 追加・削除後の結果はここで受け取る（`swym-wishlist-core-yokomo.js` 参照）。
    this.#unsubscribe = onListChange(({ detail }) => this.#applyList(detail.list));

    this.#init();
  }

  disconnectedCallback() {
    if (this.#unsubscribe) this.#unsubscribe();
  }

  #init() {
    // キャッシュがあれば `getDefaultList()` の解決を待たずに先に描画する（両者は並行に走る）。
    const cachedRaw = sessionStorage.getItem(SS_PRODUCTS_KEY);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        this.#lid = cached.lid;
        if (this.loading) this.loading.hidden = true;
        this.#setCount(cached.products.length);
        this.#renderProducts(cached.products);
      } catch {
        sessionStorage.removeItem(SS_PRODUCTS_KEY);
      }
    }

    // ヘッダーバッジ・PDP ボタンと同じリストを共有する。
    getDefaultList().then((list) => this.#applyList(list));
  }

  /** @param {{lid: string|null, listcontents?: any[]}|null} list */
  #applyList(list) {
    if (this.loading) this.loading.hidden = true;

    this.#lid = list ? list.lid : null;
    const products = list && Array.isArray(list.listcontents) ? list.listcontents : [];

    sessionStorage.setItem(SS_PRODUCTS_KEY, JSON.stringify({ lid: this.#lid, products }));

    // `products` が空でも `#renderProducts` に一本化する（分岐して個別に
    // `#setEmptyVisible(true)` だけ呼ぶと、キャッシュ描画分の古いカードが
    // グリッドに残ったまま「0件」メッセージと同時に出てしまう）。
    this.#setCount(products.length);
    this.#renderProducts(products);
  }

  #setCount(count) {
    if (this.countEl) {
      this.countEl.textContent = `(${count})`;
      this.countEl.hidden = false;
    }
    // 0件が確定するまでは出さない（実際に出すかどうかは `#renderProducts` に一本化）。
    if (count !== 0) this.#setEmptyVisible(false);
  }

  /**
   * 空状態メッセージの表示・非表示を切り替える。
   * `.swym-wishlist-page-yokomo__empty` は `{% stylesheet %}` 側で `display: flex` を
   * 無条件に指定しており、author スタイルは同じ詳細度でも UA スタイルの
   * `[hidden] { display: none }` より優先されるため、`hidden` プロパティの
   * トグルだけでは実際には隠れない（商品が残っていても表示され続けるように見える）。
   * インラインスタイルで `display` を直接制御し、CSS 側の指定より確実に上書きする。
   * @param {boolean} visible
   */
  #setEmptyVisible(visible) {
    const empty = /** @type {HTMLElement | null} */ (this.empty);
    if (!empty) return;
    empty.hidden = !visible;
    empty.style.display = visible ? '' : 'none';
  }

  /**
   * 商品カードを描画する。カード構造は `blocks/_product-card.liquid` 系の
   * コレクションカードに寄せている（`sections/swym-wishlist-page-yokomo.liquid` の
   * `{% stylesheet %}` 参照）。
   * @param {Array<{epi: string, empi: string, du: string, dt?: string, iu?: string, pr?: string, stk?: string}>} products
   */
  async #renderProducts(products) {
    const generation = ++this.#renderGeneration;

    this.grid.setAttribute('aria-busy', 'true');
    this.grid.innerHTML = '';

    if (!products || products.length === 0) {
      this.#setEmptyVisible(true);
      this.grid.setAttribute('aria-busy', 'false');
      return;
    }
    this.#setEmptyVisible(false);

    const shopifyData = await Promise.all(
      products.map((product) => {
        const key = String(product.epi);
        if (this.#productJSONCache[key]) return Promise.resolve(this.#productJSONCache[key]);
        return this.#fetchProductJSON(product.du)
          .then((data) => {
            this.#productJSONCache[key] = data;
            return data;
          })
          .catch(() => null);
      })
    );

    // 待っている間に、より新しい `#renderProducts` 呼び出しが走っていたら
    // ここで諦める。DOM を触らずに戻ることで、後着した古い結果が新しい結果の
    // 上にカードを重ねて追加する（＝商品が二重に表示される）のを防ぐ。
    if (generation !== this.#renderGeneration) return;

    const moneyFormat = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    const fragment = document.createDocumentFragment();

    products.forEach((product, index) => {
      const shopifyProduct = shopifyData[index];
      const variant =
        shopifyProduct && shopifyProduct.variants
          ? shopifyProduct.variants.find((v) => String(v.id) === String(product.epi))
          : null;

      // `product.stk` は常に undefined（保存するのは `epi`/`empi`/`du`/`dt`/`iu`/`pr` のみ）。
      // この行が使われるのは Shopify 側の商品フェッチ自体が失敗した時だけで、
      // undefined は「在庫あり扱い」に倒れる（安全側のフォールバック）。
      const inStock = variant ? variant.available : String(product.stk) !== '0';
      const price = variant ? variant.price : Number(product.pr);
      const comparePrice = variant && variant.compare_at_price > price ? variant.compare_at_price : null;
      const title = escapeHtml((shopifyProduct && shopifyProduct.title) || product.dt || '');
      const variantTitle = variant && variant.title !== 'Default Title' ? escapeHtml(variant.title) : '';
      const href = escapeHtml((shopifyProduct && shopifyProduct.url) || product.du || '#');
      const rawImage =
        (variant && variant.featured_image && variant.featured_image.src) ||
        (shopifyProduct && (shopifyProduct.featured_image || (shopifyProduct.images && shopifyProduct.images[0]))) ||
        product.iu ||
        '';
      const image = escapeHtml(rawImage);

      const card = document.createElement('div');
      card.className = 'swym-wishlist-card-yokomo';
      card.innerHTML = `
        <div class="swym-wishlist-card-yokomo__image-wrapper">
          <a href="${href}">
            ${image ? `<img src="${image}" alt="${title}" loading="lazy" class="swym-wishlist-card-yokomo__image">` : ''}
          </a>
          <button type="button" class="swym-wishlist-card-yokomo__remove" data-action="remove" aria-label="${this.dataset.labelRemove} ${title}">
            &times;
          </button>
        </div>
        <div class="swym-wishlist-card-yokomo__info">
          <a href="${href}" class="swym-wishlist-card-yokomo__title">${title}</a>
          ${variantTitle ? `<p class="swym-wishlist-card-yokomo__variant">${variantTitle}</p>` : ''}
          <div class="swym-wishlist-card-yokomo__price">
            ${comparePrice ? `<span class="swym-wishlist-card-yokomo__compare-price">${escapeHtml(formatMoney(comparePrice, moneyFormat, currency))}</span>` : ''}
            <span>${escapeHtml(formatMoney(price, moneyFormat, currency))}</span>
            <span class="swym-wishlist-card-yokomo__tax">${escapeHtml(this.dataset.taxIncluded)}</span>
          </div>
          ${!inStock ? `<span class="swym-wishlist-card-yokomo__sold-out">${escapeHtml(this.dataset.labelSoldOut)}</span>` : ''}
          <button
            type="button"
            class="button button--secondary swym-wishlist-card-yokomo__cart-btn"
            data-action="add-to-cart"
            data-variant-id="${escapeHtml(product.epi)}"
            data-product-id="${escapeHtml(product.empi)}"
            ${inStock ? '' : 'disabled'}
          >
            ${inStock ? escapeHtml(this.dataset.labelAddToCart) : escapeHtml(this.dataset.labelSoldOut)}
          </button>
        </div>
      `;

      const removeBtn = card.querySelector('[data-action="remove"]');
      removeBtn.addEventListener('click', () => this.#handleRemove(product, card));

      const cartBtn = card.querySelector('[data-action="add-to-cart"]');
      if (inStock) {
        cartBtn.addEventListener('click', () => this.#handleAddToCart(cartBtn));
      }

      fragment.appendChild(card);
    });

    this.grid.appendChild(fragment);
    this.grid.setAttribute('aria-busy', 'false');
  }

  /**
   * @param {{epi: string|number, empi: string|number, du: string}} product
   * @param {HTMLElement} card
   */
  #handleRemove(product, card) {
    // 見た目はここで即座に確定させる（楽観的更新）。ヘッダー・ボタン側、及び
    // このグリッド自体の再同期は `removeFromWishlist()` → コアの通知 → `onListChange`
    // に任せる。
    card.remove();
    const remaining = this.grid.querySelectorAll('.swym-wishlist-card-yokomo').length;
    this.#setCount(remaining);
    if (remaining === 0) this.#setEmptyVisible(true);

    removeFromWishlist(product).catch((error) =>
      console.error('[swym-wishlist-page-yokomo] removeFromWishlist', error)
    );
  }

  async #handleAddToCart(button) {
    const original = button.textContent;
    button.textContent = this.dataset.labelAdding;
    button.disabled = true;

    // `product-form.js` の add-to-cart と同じ形（`assets/product-form.js` 参照）。
    // `cart-items-component` の section id を渡さないとカートドロワーの中身が
    // 古いまま開き、`source: 'product-form-component'` と `itemCount` を渡さないと
    // `cart-icon.js` の `onCartUpdate` がヘッダーのカート件数を 0 として扱ってしまう
    // （加算ではなく絶対値として扱われるため、実機で確認済み）。
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });
      const body = await response.json();

      if (!response.ok || body.status) {
        button.textContent = this.dataset.labelCartError;
        setTimeout(() => {
          button.textContent = original;
          button.disabled = false;
        }, 2000);
        return;
      }

      button.textContent = this.dataset.labelAdded;
      this.dispatchEvent(
        new CartAddEvent({}, button.dataset.variantId || this.id || 'swym-wishlist-page-yokomo', {
          source: 'product-form-component',
          itemCount: 1,
          productId: button.dataset.productId,
          sections: body.sections,
        })
      );
      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 2000);
    } catch (error) {
      console.error('[swym-wishlist-page-yokomo] add to cart failed', error);
      button.textContent = this.dataset.labelCartError;
      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 2000);
    }
  }

  #fetchProductJSON(du) {
    const parts = (du || '').split('/products/');
    if (parts.length < 2) return Promise.reject(new Error('invalid product URL'));
    const handleAndQuery = parts[1].split('?')[0];
    const url = `${window.location.origin}${window.Shopify?.routes?.root || '/'}products/${handleAndQuery}.js`;
    return fetch(url).then((response) => {
      if (!response.ok) throw new Error('product fetch failed');
      return response.json();
    });
  }
}

if (!customElements.get('swym-wishlist-page-yokomo')) {
  customElements.define('swym-wishlist-page-yokomo', SwymWishlistPageYokomo);
}
